<template>
  <div class="emp-response-wrap">
    <analytics-topbar
      title="Chat response rate"
      :sortKey="sortByKey"
      :isFilterApplied="isFilterApplied"
      @toggleReportFilter="toggleReportFilters"
      @resetDashFilters="resetDashFilters"
    ></analytics-topbar>
    <v-row class="lifecycle-heading mx-4">
      <v-col cols="12">
        <div class="lifecycle-title">
          Chat response rate
        </div>
        <div class="lifecycle-sub">
          What percentage of chats is completed across different demographics?<br>Note: Chat response rate is based on number of chats completed. This includes multiple chats completed by an employee in different touchpoints in an employee lifecycle.
        </div>
      </v-col>
    </v-row>
    <div class="emp-responses">
      <chatSortByNav
        :sort-by-data="sortOptions"
        @onClickSortBy="onClickSortBy"
      ></chatSortByNav>
      <div class="report-body">
        <v-row class="sort-wrap">
          <v-col cols="7" class="d-flex align-center">
            <v-row no-gutters>
              <v-col cols="4">
                <div class="mt-5 mr-4 d-flex">
                  <span class="pr-1">Select</span>
                  <span v-if="sortByKey.replace(/_/g, ' ') === 'hrbp'">HRBP</span>
                  <span v-else-if="sortByKey.replace(/_/g, ' ') === 'hr manager'">HR Manager</span>
                  <span v-else>&nbsp;{{sortByKey.replace(/_/g, ' ')}}</span>:
                </div>
              </v-col>
              <v-col cols="8">
                <v-combobox
                  :items="chartData.default"
                  :menu-props="{ 'closeOnContentClick': true, 'closeOnClick': true }"
                  multiple
                  v-model="chartData.selected"
                  item-text="key"
                  hide-details
                >
                  <template v-slot:selection="{ index }">
                    <span v-if="index === 0">
                      {{chartData.selected.length}}&nbsp;of&nbsp;{{chartData.default.length}}&nbsp;<span v-if="sortByKey.replace(/_/g, ' ') === 'hrbp'">HRBP</span>
                      <span v-else-if="sortByKey.replace(/_/g, ' ') === 'hr manager'">HR Manager</span>
                      <span v-else style="text-transform:capitalize">{{sortByKey.replace(/_/g, ' ')}}</span>&nbsp;selected
                    </span>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="graph-wrap">
          <v-col cols="12" lg="9">
            <div class="chat-response-chart">
              <chat-bar-graph
                :bar-data="chartData.selected"
                :sort-by-key="sortByKey"
              ></chat-bar-graph>
            </div>
          </v-col>
          <v-col cols="12" lg="3">
            <div class="report-table-wrap" v-if="chartData.selected.length > 0">
              <div class="report-table">
                <v-row class="tableHeader ma-0">
                    <v-col cols="6" class="text-left" style="border-right:1px solid;">
                      <span class="font-weight-bold" v-if="sortByKey.replace(/_/g, ' ') === 'hrbp'">HRBP</span>
                      <span class="font-weight-bold" v-else-if="sortByKey.replace(/_/g, ' ') === 'hr manager'">HR Manager</span>
                      <span class="font-weight-bold" v-else style="text-transform:capitalize">{{sortByKey.replace(/_/g, ' ')}}</span>
                    </v-col>
                    <v-col cols="6" class="font-weight-bold">Response rate(%)</v-col>
                </v-row>
                <div class="table-body" v-if="chartData.selected.length > 0">
                  <v-row class="w-100"
                    v-for="(t,index) in chartData.selected"
                    :key="index"
                  >
                    <v-tooltip left>
                      <template v-slot:activator="{ on, attrs }">
                        <v-col cols="8" v-bind="attrs" v-on="on" style="max-width:100%;white-space: nowrap; overflow: hidden; text-overflow: ellipsis; align-self:center;">{{t.key}}</v-col>
                      </template>
                      <span>{{t.key}}</span>
                    </v-tooltip>
                    <v-col cols="4" style="align-self:center;">{{t.participation_rate}}%</v-col>
                  </v-row>
                </div>
                <div v-else class="no-sort-data">
                  Not enough data to generate anlaysis.
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row class="chat-response-trends">
          <v-col>
            <div class="trends-wrap">
              <chat-trends :trend-data="trendsData.selected"></chat-trends>
            </div>
          </v-col>
        </v-row>
      </div>
    </div>
    <div class="reportFilterDrawer">
      <reportFilters
        ref="ReportFilters"
        reportType="response_rate"
        @dashFiltersApplied="dashFiltersApplied"
        @getReportWithFilter="filterApplied"
      ></reportFilters>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import axios from 'axios';
import { mapState } from 'vuex';
import { format, subDays } from 'date-fns';
import AnalyticsTopbar from './analytics-topbar';
import ReportFilters from './analytics-filters';
import ChatSortByNav from './response-rate-component/chart-sort-by-nav';
import ChatBarGraph from './response-rate-component/chart-bar-graph';
import ChatTrends from './response-rate-component/chat-trends';

import AnalyticsMixin from './analytics-mixin';

export default {
  name: 'EmployeeChatParticipationRate',

  mixins: [AnalyticsMixin],

  components: {
    AnalyticsTopbar,
    ReportFilters,
    ChatSortByNav,
    ChatBarGraph,
    ChatTrends
  },

  data () {
    return {
      isFilterApplied: false,
      sortOptions: {},
      filterOptions: {},
      chartData: {
        default: [],
        selected: []
      },
      sortByKey: '',
      selectedfilters: [],
      reportDate: {
        start: format(subDays(new Date(), 365), 'yyyy-MM-dd'),
        end: format(new Date(), 'yyyy-MM-dd')
      },
      trendsData: {
        default: [],
        selected: {}
      }
    };
  },
  computed: {
    ...mapState({
      user: state => state.user,
      updateReportFilters: state => state.updateReportFilters
    })
  },
  watch: {
    chartData: {
      deep: true,
      handler () {
        const selectedTrendsTemp = [];
        const selectedTrends = {};
        this.$lodash.each(this.trendsData.default, (val, key) => {
          const t = {};
          t.name = key;
          t.value = val;
          selectedTrendsTemp.push(t);
        });
        this.$lodash.each(this.chartData.selected, (val, key) => {
          if (this.trendsData.default[val.key]) {
            selectedTrends[val.key] = this.trendsData.default[val.key];
          }
        });
        this.trendsData.selected = selectedTrends;
      }
    }
  },

  created () {
    this.getRateReportOtions();
    this.$store.dispatch('updateReportFilters', null);
  },

  methods: {
    resetDashFilters () {
      this.isFilterApplied = false;
      this.$refs.ReportFilters.resetFilters();
    },
    toggleReportFilters () {
      this.$refs.ReportFilters.showFilterDrawer = true;
    },
    dashFiltersApplied (val) {
      this.isFilterApplied = val;
    },
    filterApplied (val) {
      this.isFilterApplied = val;
      this.onClickSortBy(this.sortByKey);
    },
    getRateReportOtions () {
      const queryParams = {
        options: 'sort_option,filter_options',
        report_name: 'chat_response_rate'
      };
      axios.get(`${process.env.VUE_APP_API_URL}reports/get_report_options`, {
        params: queryParams
      }).then((response) => {
        if (response && response.data) {
          this.filterOptions = response.data.queryset_filter_options;
          this.sortOptions = response.data.queryset_sort_options;
        }
      }).catch((err) => {
        if (err.response.status === 429) {
          this.$store.dispatch('updateSnackbar', {
            color: 'error',
            show: true,
            text: err.response.data.error
          });
        } else {
          this.$store.dispatch('updateSnackbar', {
            color: 'error',
            show: true,
            text: 'Unable to get filter, Please try again later!'
          });
        }
      });
    },
    getGraphColor (val) {
      let color = '';
      if (val === 0 || val <= 10) {
        color = '#e6e6ed';
      } else if (val === 11 || val <= 20) {
        color = '#cdcddc';
      } else if (val === 21 || val <= 30) {
        color = '#b4b4ca';
      } else if (val === 31 || val <= 40) {
        color = '#9b9bb9';
      } else if (val === 41 || val <= 50) {
        color = '#8282a7';
      } else if (val === 51 || val <= 60) {
        color = '#696996';
      } else if (val === 61 || val <= 70) {
        color = '#505084';
      } else if (val === 71 || val <= 80) {
        color = '#363672';
      } else if (val === 81 || val <= 90) {
        color = '#1d1d61';
      } else if (val === 91 || val <= 100) {
        color = '#050550';
      }
      return color;
    },
    onClickSortBy (type) {
      console.log(this.updateReportFilters);
      if (type) {
        this.sortByKey = type;
        const queryParams = {
          sort_key: type,
          fields: 'chat_participation_stats,chat_participation_trends',
          segment_filter: this.updateReportFilters && this.updateReportFilters.segment_filter ? this.updateReportFilters.segment_filter : undefined,
          initiated_date_start: this.updateReportFilters ? this.updateReportFilters.initiated_date_start : undefined,
          initiated_date_end: this.updateReportFilters ? this.updateReportFilters.initiated_date_end : undefined,
          user__profile__high_potential_emp: this.updateReportFilters ? this.updateReportFilters.high_potential_emp : undefined
        };
        const filters = Object.assign({}, this.updateReportFilters);
        if (filters && filters.filters) {
        // filters.filters have the dynamic filters value
          delete filters.filters;
        }
        if (this.updateReportFilters && this.updateReportFilters.filters) {
          this.$lodash.each(this.updateReportFilters.filters, (v, k) => {
            if (v.length > 0 && k !== 'session__stage__title') {
              queryParams[`user__profile__${k}__in`] = v.join();
            } else {
              queryParams.stage__title__in = v.join();
            }
          });
        }
        axios.get(`${process.env.VUE_APP_API_URL}reports/get_participation_report`, {
          params: queryParams
        }).then((response) => {
          if (response && response.data) {
            const temp = [];
            this.$lodash.each(response.data.chat_participation_stats, (val) => {
              const t = {
                ...val
              };
              t.color = this.getGraphColor(val.participation_rate);
              temp.push(t);
            });
            this.chartData.default = temp;
            const selectedItems = [];
            this.$lodash.each(temp, (val, index) => {
              if (index < 10) {
                selectedItems.push(val);
              }
            });
            this.chartData.selected = selectedItems;
            this.trendsData.default = response.data.chat_participation_trends;
          }
        }).catch((err) => {
          if (err.response.status === 429) {
            this.$store.dispatch('updateSnackbar', {
              color: 'error',
              show: true,
              text: err.response.data.error
            });
          } else {
            this.$store.dispatch('updateSnackbar', {
              color: 'error',
              show: true,
              text: 'Unable to get report, Please try again later!'
            });
          }
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.emp-responses {
  box-shadow: 0 4px 16px 11px rgba(0, 0, 0, 0.06),0 4px 8px rgba(0, 0, 0, 0.1) !important;
  margin: 12px;
}
.emp-response-wrap {
  background-color: var(--bg);
  .lifecycle-heading {
    margin: 0;
    .lifecycle-title {
      color: var(--color-text);
      // font-size: var(--content-size);
      font-size: var(--stats-size-1);
    }
    .lifecycle-sub {
      color: var(--color-text);
      font-size: var(--content-size);
      padding: 8px 0;
    }
  }
  .report-body {
    background: var(--bg-panel);
    margin: 0 16px;
    .sort-wrap {
      margin: 0;
    }
    .graph-wrap {
      margin: 0;
    }
    .chat-response-chart {
      height: 30rem;
      background: var(--bg);
      border-radius: 6px;
      position: relative;
    }
    .report-table-wrap {
      border-radius: 6px;
      height: 30rem;
      background: var(--bg);
      border: 1px solid rgba(0,0,0,0.2);
      .report-table {
        .tableHeader {
          background-color: var(--primary-color-1);
          // padding: 8px;
        }
        .table-head, .table-body {
          tr {
            display: grid;
            grid-template-columns: 3fr 1fr;
            border-top: 1px solid rgba(0,0,0,0.1);
            td,th {
              padding: 8px 0px 8px 16px;
              display: grid;
              align-items: center;
              justify-items: start;
              font-size: var(--content-size);
              color: var(--color-text);
            }
          }
        }
        .table-head {
          display: grid;
          min-height: 50px;
          max-height: 50px;
          tr {
            th {
              text-transform: capitalize;
            }
            &:first-child {
              border-top: 0;
            }
          }
        }
        .table-body {
          min-height: 400px;
          max-height: 400px;
          overflow-y: auto;
          display: grid;
          padding-left: 8px;
        }
        .no-sort-data {
          text-align: center;
          font-size: var(--content-size);
          color: var(--color-text);
          display: grid;
          align-items: center;
          justify-content: center;
          height: 350px;
          border-top: 1px solid var(--color-text);
        }
      }
    }
    .chat-response-trends {
      margin: 0;
      .trends-wrap {
        border-radius: 6px;
        background: var(--bg);
      }
    }
  }
}
</style>
